
import { useState, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { generateAndStoreThumbnail } from '@/utils/thumbnailGenerator';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Image, Upload, X, Sparkles, Copy, Check } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const THUMBNAIL_SPEC_PROMPT =
  'Generate a landscape image with a 3:2 aspect ratio, 1500 × 1000 px, in JPG or WebP format, under 2 MB.';

interface ThumbnailUploadProps {
  currentThumbnail: string | null;
  onThumbnailChange: (url: string) => void;
  /**
   * When true, renders a slim upload/replace/remove control (no large preview
   * box) — used where a live card preview already shows the image.
   */
  compact?: boolean;
}

export const ThumbnailUpload = ({ currentThumbnail, onThumbnailChange, compact = false }: ThumbnailUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [specCopied, setSpecCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleCopySpec = async () => {
    try {
      await navigator.clipboard.writeText(THUMBNAIL_SPEC_PROMPT);
      setSpecCopied(true);
      toast({
        title: "Spec copied ✓",
        description: "Paste it into your AI image generator.",
      });
      setTimeout(() => setSpecCopied(false), 2000);
    } catch {
      toast({
        title: "Copy failed",
        description: "Please copy the spec manually.",
        variant: "destructive",
      });
    }
  };

  const handleThumbnailUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Please upload an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "Image size should be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${crypto.randomUUID()}.${fileExt}`;
      const filePath = `thumbnails/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('insight_attachments')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('insight_attachments')
        .getPublicUrl(filePath);

      onThumbnailChange(publicUrl);

      // Bake a permanent WebP thumbnail so the insight cards load from the fast
      // object CDN. Best-effort: the card falls back to on-the-fly rendering if
      // this fails or hasn't finished yet.
      try {
        await generateAndStoreThumbnail(publicUrl);
      } catch (thumbErr) {
        console.error('Error baking insight thumbnail:', thumbErr);
      }

      toast({
        title: "Success",
        description: "Thumbnail uploaded successfully",
      });
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      toast({
        title: "Error",
        description: "Failed to upload thumbnail",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const removeThumbnail = () => {
    onThumbnailChange('');
  };

  const specBanner = (
    <Alert className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-violet-50 text-indigo-900 [&>svg]:text-indigo-500">
      <Sparkles className="h-4 w-4" />
      <AlertTitle className="text-indigo-900">
        Thumbnail spec — use this when generating with AI
      </AlertTitle>
      <AlertDescription className="text-indigo-800/90">
        <p className="text-left">
          Landscape <span className="font-semibold">3:2 ratio</span> ·{' '}
          <span className="font-semibold">1500 × 1000 px</span> (min 1200 × 800) · JPG or WebP · ideally under 2 MB (max 5 MB)
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleCopySpec}
          className="mt-3 h-8 border-indigo-300 bg-white/70 text-indigo-700 hover:bg-white hover:text-indigo-800"
        >
          {specCopied ? (
            <Check className="h-3.5 w-3.5 mr-1.5" />
          ) : (
            <Copy className="h-3.5 w-3.5 mr-1.5" />
          )}
          {specCopied ? 'Copied' : 'Copy spec for AI'}
        </Button>
      </AlertDescription>
    </Alert>
  );

  const bigBox = (
    <div className="relative w-full aspect-[3/2] overflow-hidden bg-gray-100">
      {currentThumbnail ? (
        <>
          <img
            src={currentThumbnail}
            alt="Thumbnail preview"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleButtonClick}
                className="bg-white hover:bg-gray-100"
              >
                <Upload className="w-4 h-4 mr-2" />
                Replace
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={removeThumbnail}
              >
                <X className="w-4 h-4 mr-2" />
                Remove
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
          <Image className="w-12 h-12 mb-4" />
          <p className="text-sm mb-4">No image uploaded</p>
          <Button
            variant="secondary"
            onClick={handleButtonClick}
            disabled={isUploading}
          >
            {isUploading ? (
              <span className="flex items-center gap-2">
                <Upload className="w-4 h-4 animate-spin" />
                Uploading...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload Thumbnail
              </span>
            )}
          </Button>
        </div>
      )}
    </div>
  );

  const compactControl = currentThumbnail ? (
    <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600">
        <Check className="h-4 w-4 text-green-500" />
        Thumbnail set
      </span>
      <div className="ml-auto flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleButtonClick}
          disabled={isUploading}
        >
          <Upload className={`mr-1.5 h-3.5 w-3.5 ${isUploading ? 'animate-spin' : ''}`} />
          {isUploading ? 'Uploading…' : 'Replace'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={removeThumbnail}
          className="text-red-500 hover:text-red-600"
        >
          <X className="mr-1.5 h-3.5 w-3.5" />
          Remove
        </Button>
      </div>
    </div>
  ) : (
    <button
      type="button"
      onClick={handleButtonClick}
      disabled={isUploading}
      className="flex w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-gray-500 transition-colors hover:border-indigo-300 hover:bg-indigo-50/40 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {isUploading ? (
        <>
          <Upload className="h-6 w-6 animate-spin" />
          <span className="text-sm">Uploading…</span>
        </>
      ) : (
        <>
          <Image className="h-8 w-8" />
          <span className="text-sm font-medium">Upload thumbnail</span>
          <span className="text-xs text-gray-400">JPG or WebP · 3:2 · 1500 × 1000 px</span>
        </>
      )}
    </button>
  );

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {specBanner}

        {compact ? compactControl : bigBox}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleThumbnailUpload}
          disabled={isUploading}
        />
      </div>
    </TooltipProvider>
  );
};
